import { Component, Input, OnInit, EventEmitter, Output } from '@angular/core';
import { Subject } from 'rxjs';
import {
  config,
  Address,
  MemberQuery,
  AddressQuery,
  AddressType,
  MemberDetails,
  PurchaseService,
  MarketplaceItemExtended,
  AttributeValue,
  MarketplaceBasketCustomFieldModel,
  ErrorStateService,
  BalanceService,
  TransactionSuccessService,
  MarketplaceQuery,
  MarketplaceService,
  TermsAndCondtionsType,
  RewardsWalletService,
} from '@fgb/core';
import { take, takeUntil, tap } from 'rxjs/operators';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { RegexUS } from '@fgb/portal-component-library/src/lib/shared/utilities';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { CollectAddressModal } from '@fgb/portal-component-library/src/lib/loyalty/collect-address-modal';

@Component({
  selector: 'fgbcl-marketplace-purchase-confirmation',
  templateUrl: './marketplace-purchase-confirmation.component.html',
  styleUrls: ['./marketplace-purchase-confirmation.component.scss'],
})
export class MarketplacePurchaseConfirmationComponent implements OnInit {
  config: any = config;
  addresses: Address[];
  shippingAddressForm: UntypedFormGroup;
  shippingAddress: Address;
  defaultAddressForm: UntypedFormGroup;
  defaultAddress: Address;
  member: MemberDetails;
  clicked = false;
  termsLink: string;
  TermsAndCondtionsType = TermsAndCondtionsType;

  @Input() templateSelected: string;
  @Input() marketplaceItem: MarketplaceItemExtended;
  @Input() marketplaceItemType: string;
  @Input() attributeMappingID: number | undefined;
  @Input() selectedAttributes: AttributeValue[];
  @Input() validCustomFields: MarketplaceBasketCustomFieldModel[];
  @Input() purchaseQuantity: number;
  @Input() addToCart: boolean;

  @Output() purchaseLoading = new EventEmitter<boolean>();
  @Output() addToCartTermsConfirmed = new EventEmitter<boolean>();

  private _destroyed$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private purchaseService: PurchaseService,
    private memberQuery: MemberQuery,
    private addressQuery: AddressQuery,
    private modalService: NgbModal,
    private router: Router,
    public errorService: ErrorStateService,
    private balanceService: BalanceService,
    private transactionSuccessService: TransactionSuccessService,
    private marketplaceQuery: MarketplaceQuery,
    private marketplaceService: MarketplaceService,
    private rewardsWalletService: RewardsWalletService
  ) {}

  ngOnInit() {
    this.memberQuery
      .selectMemberDetails()
      .pipe(
        tap((z) => {
          if (z) {
            this.defaultAddress = {
              FirstName: z.FirstName,
              Surname: z.Surname,
              Street: z.Street,
              Town: z.Town,
              County: z.County,
              Postcode: z.PostCode,
              Country: z.Country,
              HomeNumber: z.HomeNumber,
              EmailAddress: z.EmailAddress1,
              PortalId: z.PortalId,
              ClubId: z.ClubId,
              AddressName: 'Default Address',
              AddressType: AddressType.DefaultAddress,
            } as Address;
          }
        })
      )
      .toPromise();

    this.addressQuery
      .selectAddresses([AddressType.DefaultAddress])
      .pipe(
        take(1),
        tap((addresses: Address[]) => {
          this.addresses = addresses;
          this.memberQuery
            .selectMemberDetails()
            .pipe(
              take(1),
              tap((md) => {
                if (md) {
                  this.member = md;
                  this.shippingAddress = this._getDefaultShippingAddress();
                  this.shippingAddressForm = this.formBuilder.group({
                    addressName: [this.shippingAddress.AddressName, Validators.required],
                    emailAddress: [this.shippingAddress.EmailAddress, Validators.email],
                    street: [this.shippingAddress.Street, Validators.required],
                    town: [this.shippingAddress.Town, Validators.required],
                    county: [this.shippingAddress.County, Validators.required],
                    country: [this.shippingAddress.Country, Validators.required],
                    postcode: [this.shippingAddress.Postcode, [Validators.required, Validators.pattern(RegexUS.postCode)]],
                    firstName: [this.shippingAddress.FirstName, Validators.required],
                    surname: [this.shippingAddress.Surname, Validators.required],
                    homeNumber: [this.shippingAddress.HomeNumber, Validators.required],
                    portalId: [this.member.PortalId],
                    clubId: [this.member.ClubId],
                  });
                }
              })
            )
            .subscribe();
        })
      )
      .subscribe();
  }

  private _getDefaultShippingAddress(): Address {
    if (this.addresses) {
      let shippingAddress1 = this.addresses.filter((a) => a.AddressType === AddressType.ShippingAddress1)[0] as Address;
      if (shippingAddress1) {
        return shippingAddress1;
      }

      let shippingAddress2 = this.addresses.filter((a) => a.AddressType === AddressType.ShippingAddress2)[0] as Address;
      if (shippingAddress2) {
        return shippingAddress2;
      }
    }
    return {
      FirstName: '',
      Surname: '',
      Street: '',
      Town: '',
      County: '',
      Postcode: '',
      Country: '',
      HomeNumber: '',
      EmailAddress: '',
      PortalId: this.member.PortalId,
      ClubId: this.member.ClubId,
      AddressType: AddressType.ShippingAddress1,
      AddressName: 'Shipping Address',
    } as Address;
  }

  openAddressCollectionModal(
    address: Address,
    orderId: string,
    saveMemberDetailsAddress: boolean,
    saveShippingAddress: boolean,
    saveOrderAddress: boolean
  ) {
    const modal = this.modalService.open(CollectAddressModal, { ariaLabelledBy: 'modal-basic-title', centered: true });
    modal.componentInstance.address = address;
    modal.componentInstance.orderId = orderId;
    modal.componentInstance.saveMemberDetailsAddress = saveMemberDetailsAddress;
    modal.componentInstance.saveShippingAddress = saveShippingAddress;
    modal.componentInstance.saveOrderAddress = saveOrderAddress;
    modal.componentInstance.submittedAddress.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      this.closeModal();
      this.continueOrder(orderId);
    });
  }

  openTerms(content: any) {
    this.modalService.open(content, { centered: true });
  }

  purchase(): void {
    this.clicked = true;

    if (this.addToCart) {
      this.modalService.dismissAll();
      this.addToCartTermsConfirmed.emit(true);
    } else {
      this.purchaseLoading.emit(true);
      let price = this.marketplaceQuery.getPrice(this.marketplaceItem.Id);

      if (price == undefined) {
        this.errorService.clearErrors();
        this.errorService.addError('Failed to purchase item');
        this.resetLoading();
        return;
      }

      let cost = price * this.purchaseQuantity;

      this.marketplaceService
        .purchaseItemWithProviderResopnse(
          this.marketplaceItem.Id,
          this.purchaseQuantity,
          this.attributeMappingID,
          undefined,
          this.validCustomFields ? this.validCustomFields : [],
          this.templateSelected === 'confirmContentCardPayment' ? true : false
        )
        .then((response) => {
          if (response && response.Success) {
            this.balanceService.debitBalance(this.config.purseConfig.virtualLoyalty, cost);

            let item = this.marketplaceQuery.getEntity(this.marketplaceItem.Id);
            if (item) {
              this.transactionSuccessService.setTransaction(item, this.purchaseQuantity, cost, response.OrderId);
            }
            this.marketplaceService.disableMarketplaceStoreCache();
            this.rewardsWalletService.disableRewardsWalletStoreCache();
            this.router.navigate(['rewards/marketplace/purchase/success/']);
          } else if (response) {
            if (response.ProviderActionInformation.NextAction) {
              if (response.ProviderActionInformation.NextAction == 20) {
                this.openAddressCollectionModal(this.shippingAddress, response.OrderId, false, true, false);
              } else if (response.ProviderActionInformation.NextAction == 40) {
                this.openAddressCollectionModal(this.defaultAddress, response.OrderId, true, false, false);
              }
            } else {
              this.errorService.clearErrors();
              this.errorService.addError(!!response.ErrorMessage ? response.ErrorMessage : 'Purchase failed');
            }
          }
        })

        .finally(() => {
          this.resetLoading();
        });
    }
  }

  continueOrder(orderId: string) {
    let price = this.marketplaceQuery.getPrice(this.marketplaceItem.Id);

    if (price == undefined) return;

    let cost = price * this.purchaseQuantity;

    this.purchaseLoading.emit(true);
    this.purchaseService
      .continueOrder(orderId)
      .toPromise()
      .then((continueResponse) => {
        if (continueResponse.Data.Success) {
          this.balanceService.debitBalance(this.config.purseConfig.virtualLoyalty, cost);
          let item = this.marketplaceQuery.getEntity(this.marketplaceItem.Id);
          if (item) {
            this.transactionSuccessService.setTransaction(item, this.purchaseQuantity, cost);
          }
          this.marketplaceService.disableMarketplaceStoreCache();
          this.router.navigate(['rewards/marketplace/purchase/success/']);
        } else {
          this.errorService.clearErrors();
          this.errorService.addError(!!continueResponse.ErrorMessage ? continueResponse.ErrorMessage : 'Purchase failed');
        }
      })
      .finally(() => {
        this.purchaseLoading.emit(false);
      });
  }

  closeModal() {
    this.modalService.dismissAll();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private resetLoading() {
    this.clicked = false;
    this.purchaseLoading.emit(false);
  }
}
